// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.
import { useState } from 'react'
import { isAlreadyDeleted, isNoteLocked, ownerApi, OwnerApiError } from 'lib/owner-notes/api'
import type { OwnerTrack } from 'lib/owner-notes/analytics'
import {
  noteAfterUndo,
  OWN_PROOFREAD_NOTHING_TO_UNDO,
  OWN_PROOFREAD_UNDO_FAILED,
  type ProofreadUndo,
} from 'lib/owner-notes/band-copy'
import { refreshOwnNotes } from 'lib/owner-notes/own-note'
import type { OwnerNoteOwn } from 'lib/dealhunt/types'

export interface OwnNoteActions {
  busy: boolean
  error: string | null
  /** The appeal went through this mount: "Sent to a moderator." replaces the button. */
  appealed: boolean
  /** A write came back 409 note_locked / note_removed (#123): the note is the moderator's now. */
  locked: boolean
  appeal: () => Promise<void>
  remove: () => Promise<void>
  /** The proofread Undo's answer, for `shownOwnNote` — null until an Undo succeeds. */
  undone: ProofreadUndo | null
  /** The Undo's own refusal, shown beside the proofread notice (not the card's bottom line). */
  undoError: string | null
  undoProofread: (note: OwnerNoteOwn) => Promise<void>
}

/** Why an Undo did not go through, in the author's words. */
export function proofreadUndoError(e: unknown): string {
  if (isNoteLocked(e)) {
    return e.message
  }
  if (e instanceof OwnerApiError && e.status === 409) {
    return OWN_PROOFREAD_NOTHING_TO_UNDO
  }
  return e instanceof OwnerApiError && e.status === 0 ? e.message : OWN_PROOFREAD_UNDO_FAILED
}

/**
 * The author's writes on their own note — "Ask for a human look" (appeal) and Delete — with one
 * busy flag, DealHunt's sentence on failure and the #123 lock. Shared by `OwnNoteCard` (My gear,
 * the product page) and the member hub's Gear rows (`GearOwnerRow`), so both refuse and recover
 * the same way. `trackOwner('appeal')` fires as before; on a community page it is a no-op (no
 * catalog page type — `useOwnerTrack`).
 */
export function useOwnNoteActions(
  noteId: string,
  {
    trackOwner,
    onAppealed,
    onDeleted,
  }: { trackOwner: OwnerTrack; onAppealed?: () => void; onDeleted?: () => void }
): OwnNoteActions {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [appealed, setAppealed] = useState(false)
  const [locked, setLocked] = useState(false)
  const [undone, setUndone] = useState<ProofreadUndo | null>(null)
  const [undoError, setUndoError] = useState<string | null>(null)

  const run = async (fn: () => Promise<void>): Promise<void> => {
    if (busy) {
      return
    }
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (e) {
      setError(e instanceof OwnerApiError ? e.message : 'Something went wrong — try again.')
      if (isNoteLocked(e)) {
        // DealHunt's sentence stays on screen; the buttons that would hit the same refusal go.
        setLocked(true)
      }
    } finally {
      setBusy(false)
    }
  }

  // DealHunt's own 404 (`isAlreadyDeleted`: a repeat, another tab) is the outcome the author asked
  // for — it lands like a delete, never as an error under a review that is already gone.
  const remove = (): Promise<void> =>
    run(async () => {
      try {
        await ownerApi.remove(noteId)
      } catch (e) {
        if (!isAlreadyDeleted(e)) {
          throw e
        }
      }
      onDeleted?.()
    })

  const appeal = (): Promise<void> =>
    run(async () => {
      await ownerApi.appeal(noteId)
      trackOwner('appeal')
      setAppealed(true)
      onAppealed?.()
    })

  /**
   * Put back the author's own words (the AI proofread, 2026-10-06). The card swaps to DealHunt's
   * answer at once; `/me` is asked again so every other surface on the page catches up. On a
   * refusal the notice stays with one short line beside it; a #123 lock also takes the controls.
   */
  const undoProofread = async (note: OwnerNoteOwn): Promise<void> => {
    if (busy) {
      return
    }
    setBusy(true)
    setUndoError(null)
    try {
      const body = await ownerApi.undoProofread(noteId)
      const after = noteAfterUndo(body, note)
      if (after) {
        setUndone({ id: note.id, fromText: note.note, to: after })
      }
      refreshOwnNotes()
    } catch (e) {
      setUndoError(proofreadUndoError(e))
      if (isNoteLocked(e)) {
        setLocked(true)
      }
    } finally {
      setBusy(false)
    }
  }

  return { busy, error, appealed, locked, appeal, remove, undone, undoError, undoProofread }
}
